import { findTerms, isIgnored, type IgnoredTerm, type TermHit, type TermIndex } from '@dispositio/core';
import type { Node as PMNode } from '@tiptap/pm/model';
import { PluginKey } from '@tiptap/pm/state';
import { suggestExtension, type SuggestAct, type SuggestLive, type SuggestState } from './suggest';

/**
 * Mögliche Verweise im Texteditor: Namen und Vorkommen von Elementen, die im Text stehen, aber nicht verknüpft sind, sind als Vorschlag
 * markiert (`suggest.ts`); ✓ verknüpft, ✕ ignoriert die Stelle.
 */

export type TermAct = SuggestAct;

export interface TermLive extends SuggestLive<TermHit> {
  index: TermIndex;
  /** bewusst nicht verknüpfte Stellen dieses Dokuments */
  ignored: readonly IgnoredTerm[] | undefined;
  /** Tooltip der Stelle */
  title: (hit: TermHit) => string;
}

export const termKey = new PluginKey<SuggestState<TermHit>>('termMarks');

/** Allein stehende Verweise stehen als Text im Editor (`[@schlüssel]`, `@schlüssel`); was darin steht, ist kein Vorkommen. */
const REF = /\[@[A-Za-z][A-Za-z0-9_]{0,29}\]|(?<![\w@[])@[A-Za-z][A-Za-z0-9_]{0,29}/g;

/** Nicht verknüpfte, nicht ignorierte Vorkommen im Dokument (mit ihrer Lage im Dokument). Zeilenumbrüche trennen wie Absätze. */
export function termHits(doc: PMNode, index: TermIndex, ignored: readonly IgnoredTerm[] | undefined): TermHit[] {
  const out: TermHit[] = [];
  doc.descendants((node, pos) => {
    if (!node.isTextblock) return true;
    if (node.type.spec.code) return false;
    const text = node.textBetween(0, node.content.size, '\n', '\n');
    const linked: [number, number][] = [];
    node.forEach((child, offset) => {
      if (child.isText && child.marks.some((m) => m.type.name === 'refLink')) linked.push([offset, offset + child.nodeSize]);
    });
    for (const m of text.matchAll(REF)) linked.push([m.index, m.index + m[0].length]);
    for (const h of findTerms(text, linked, index)) if (!isIgnored(h, ignored)) out.push({ ...h, from: pos + 1 + h.from, to: pos + 1 + h.to });
    return false;
  });
  return out;
}

export const termMarks = (live: { current: TermLive }) =>
  suggestExtension<TermHit, TermLive>(
    {
      name: 'termMarks',
      kind: 'ref',
      key: termKey,
      hits: (doc, l) => termHits(doc, l.index, l.ignored),
      mark: (h, l) => ({ class: `rt-term${h.entityIds.length > 1 ? ' ambiguous' : ''}`, 'data-term': h.term, title: l.title(h) }),
      word: (h) => h.term,
      testId: 'term',
    },
    live,
  );
